import { z } from 'zod';
import { EmailSchema, HistoryEntrySchema, IDSchema, IsoTimestampSchema, SchemaVersionSchema, } from './shared.js';
/**
 * Phase C (SO-001 commission build) — the **contractor** entity.
 *
 * A contractor is a commission-earning sales rep, keyed by the same email used
 * across the system as `account.assignedRepEmail` and
 * `commissionStatement.recipientRepEmail`. Until now a "rep" was only an Entra
 * user + role assignment + `user_profile`; nothing recorded the facts the
 * SO-001 plan needs about the *commission relationship*:
 *
 *   - `startDate` — the contractor's commission start. Drives §5 ramp-up
 *     automation (which ramp-up milestone month applies) and tenure-based
 *     reporting. A `YYYY-MM-DD` calendar date (no time component).
 *   - `status` — `active` | `inactive`. SO-001 §9 inactive status. When a
 *     contractor goes `inactive` they stop accruing new-deal commission; the
 *     flag is **admin-only** visibility (superAdmin/opsAdmin) per the locked
 *     Phase C decision — never surfaced to individual reps.
 *   - `referredBy` — who referred this contractor (a name or email). Supports
 *     the separate referral / finder's-fee program; recorded here because the
 *     contractor record is the natural home for the relationship.
 *
 * Ramp-up automation, §8 minimum-performance benchmarks, and QBO refund
 * auto-sync (the rest of Phase C) build ON this entity in later PRs. This PR is
 * the foundation: the entity, its admin CRUD, and the status lifecycle.
 *
 * Mirrors the `contractors` Drizzle table — kept byte-aligned by
 * `ci/zod-drizzle-drift`.
 */
/** Calendar date with no time component, `YYYY-MM-DD`. */
export const DateOnlySchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
// RIG-387 — the active/inactive status concept is retired (commission is fully
// implicit: salesRep ⟹ earns; "stop earning" = removal from the Entra group).
// The status / inactiveAt / inactiveReason columns are dropped (migration 0055).
export const ContractorSchema = z.object({
    id: IDSchema,
    type: z.literal('contractor'),
    schemaVersion: SchemaVersionSchema,
    /** The commission identity — matches assignedRepEmail / recipientRepEmail. */
    email: EmailSchema,
    displayName: z.string().min(1).max(120),
    /** Commission start date (drives §5 ramp-up). `YYYY-MM-DD`. */
    startDate: DateOnlySchema,
    /** Referral program — who referred this contractor (name or email). */
    referredBy: z.string().max(200).optional(),
    /** Free-form admin notes. */
    notes: z.string().max(2000).optional(),
    history: z.array(HistoryEntrySchema).default([]),
    createdAt: IsoTimestampSchema,
    createdBy: z.string(),
    updatedAt: IsoTimestampSchema,
    updatedBy: z.string(),
    deletedAt: IsoTimestampSchema.nullable().optional(),
    version: z.number().int().positive(),
});
//# sourceMappingURL=contractor.js.map